import { ApplyButton } from 'legacy/lut/components/widgets/public/partner360/editors/ApplyButton'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { FC, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { MultiSelectDynamic } from 'core-components/library/multiSelect'
import { GeoMapping, ProductTree } from 'partner360/components/filters'

import { TRANSLATION_KEYS } from '../../../../../../translations'

import { useFilters } from './hooks/useFilters'
import { useMultiselectCruds } from './hooks/useMultiselectCruds'
import { IFiltersContainerProps } from './models/filtersContainerProps'

import styles from './FiltersContainer.module.scss'

export const FiltersContainer: FC<IFiltersContainerProps> = ({ onFilterChange, data, rtm }) => {
  /** -------------------- Selectors ----------------------- */
  const { currentFYear } = useSelector(selectJBPSettings)

  /** -------------------- Hooks ----------------------- */
  const {
    geoMappingsFilter,
    setGeoMappingsFilter,
    selectedGeoMappings,
    setSelectedGeoMappings,
    productTreeFilter,
    setProductTreeFilter,
    selectedProductTree,
    setSelectedProductTree,
    pbmFilter,
    setPbmFilter,
    partnerIdFilter,
    setPartnerIdFilter,
    managerFilter,
    setManagerFilter,
  } = useFilters()

  const { partnerIdCrud, managersCrud, pbmsCrud } = useMultiselectCruds(data)

  /** -------------------- Methods ----------------------- */
  const handleChangeGeoMappingFilter = useCallback(
    (rootSelectedItems: string[], allSelectedItems: string[]) => {
      setSelectedGeoMappings(rootSelectedItems)
      setGeoMappingsFilter(allSelectedItems)
    },
    [setGeoMappingsFilter, setSelectedGeoMappings]
  )

  const handleChangeProductTreeFilter = useCallback(
    (rootSelectedItems: string[], allSelectedItems: string[]) => {
      setSelectedProductTree(rootSelectedItems)
      setProductTreeFilter(allSelectedItems)
    },
    [setProductTreeFilter, setSelectedProductTree]
  )

  const handleApplyFilters = useCallback(() => {
    onFilterChange({
      geoMappingsFilter,
      productTreeFilter,
      pbmFilter,
      partnerIdFilter,
      managerFilter,
    })
  }, [
    onFilterChange,
    geoMappingsFilter,
    productTreeFilter,
    pbmFilter,
    partnerIdFilter,
    managerFilter,
  ])

  const handleResetFilters = useCallback(() => {
    setGeoMappingsFilter([])
    setProductTreeFilter([])
    setPbmFilter([])
    setPartnerIdFilter([])
    setManagerFilter([])
    onFilterChange({
      geoMappingsFilter: [],
      productTreeFilter: [],
      pbmFilter: [],
      partnerIdFilter: [],
      managerFilter: [],
    })
  }, [
    onFilterChange,
    setGeoMappingsFilter,
    setProductTreeFilter,
    setPbmFilter,
    setPartnerIdFilter,
    setManagerFilter,
  ])

  return (
    <div className={styles.root}>
      <GeoMapping
        className={styles.geoMapping}
        popupClassName={styles.geoMappingPopup}
        values={selectedGeoMappings}
        onSelectChange={handleChangeGeoMappingFilter}
        label={<Translation text={TRANSLATION_KEYS.globalPartnerDetails.filters.geoMapping} />}
      />
      <ProductTree
        partnerType={rtm}
        fYear={currentFYear}
        className={styles.productTree}
        popupClassName={styles.productTreePopup}
        values={selectedProductTree}
        onSelectChange={handleChangeProductTreeFilter}
        label={<Translation text={TRANSLATION_KEYS.globalPartnerDetails.filters.productTree} />}
      />
      <MultiSelectDynamic
        classes={{
          controlClasses: {
            root: styles.partnerIdMultiselect,
          },
        }}
        label="Partners"
        value={partnerIdFilter}
        source={partnerIdCrud}
        caption={'value'}
        fieldsToSearch={['id', 'value']}
        onChange={setPartnerIdFilter}
      />
      <MultiSelectDynamic
        classes={{
          controlClasses: {
            root: styles.pbmMultiSelect,
          },
        }}
        label="PBM"
        value={pbmFilter}
        source={pbmsCrud}
        caption={'value'}
        onChange={setPbmFilter}
      />
      <MultiSelectDynamic
        classes={{
          controlClasses: {
            root: styles.managerMultiselect,
          },
        }}
        label="Manager"
        value={managerFilter}
        source={managersCrud}
        caption={'value'}
        onChange={setManagerFilter}
      />

      <div className={styles.buttonsContainer}>
        <ApplyButton onClick={handleApplyFilters} className={styles.apply} />
        <Button onClick={handleResetFilters}>
          <Translation text={TRANSLATION_KEYS.globalPartnerDetails.filters.resetButton} />
        </Button>
      </div>
    </div>
  )
}
